import { useCallback, useEffect, useRef, useState } from "react";
import { fetchMessageBodyWithCache } from "@/lib/mail/fetchMessageBodyWithCache";
import {
  readCachedMessageBody as readLocalBody,
  readCachedMessageBodyAsync as readLocalBodyAsync,
} from "@/lib/mail/bodyCacheLookup";
import { subscribeMessageBodyCache } from "@/lib/cache/messageBodyCache";
import { isFetchedBodyComplete } from "@/lib/mail/messageBodyCacheStatus";
import {
  pauseClientHydration,
  resumeClientHydration,
} from "@/lib/mail/primeVisibleReadyBodies";
import type { MailMessageDetail, MailMessageSummary } from "@/lib/types/mail";

type ReloadOptions = {
  force?: boolean;
  interactive?: boolean;
};

function summaryToPartialDetail(summary: MailMessageSummary): MailMessageDetail {
  return {
    uid: summary.uid,
    subject: summary.subject,
    from: summary.from,
    to: "",
    cc: "",
    messageId: "",
    date: summary.date,
    bodyHtml: "",
    bodyText: "",
    seen: summary.seen,
    hasAttachments: summary.hasAttachments ?? false,
    attachments: [],
  };
}

function previewMatchesReader(
  preview: MailMessageSummary | null | undefined,
  accountId: number,
  readerUid: number
): boolean {
  if (!preview || readerUid <= 0) return false;
  if (preview.accountId != null && preview.accountId !== accountId) return false;
  const bodyUid = preview.imapBodyUid ?? preview.uid;
  return preview.uid === readerUid || bodyUid === readerUid;
}

function resolvePreviewForReader(
  preview: MailMessageSummary | null | undefined,
  accountId: number,
  readerUid: number
): MailMessageSummary | null {
  if (!preview) return null;
  return previewMatchesReader(preview, accountId, readerUid) ? preview : null;
}

function messageMatchesReader(
  message: MailMessageDetail | null | undefined,
  readerUid: number,
  preview?: MailMessageSummary | null
): boolean {
  if (!message || readerUid <= 0) return false;
  if (message.uid === readerUid) return true;
  if (preview?.uid === readerUid && message.uid === preview.uid) return true;
  const bodyUid = preview?.imapBodyUid;
  return bodyUid != null && bodyUid > 0 && message.uid === bodyUid;
}

function mergePreviewWithExisting(
  preview: MailMessageSummary,
  existing: MailMessageDetail | null,
  readerUid: number
): MailMessageDetail {
  const partial = summaryToPartialDetail(preview);
  if (existing && isFetchedBodyComplete(existing) && messageMatchesReader(existing, readerUid, preview)) {
    return {
      ...partial,
      uid: existing.uid,
      bodyHtml: existing.bodyHtml,
      bodyText: existing.bodyText,
      attachments: existing.attachments,
      to: existing.to || partial.to,
      cc: existing.cc || partial.cc,
      messageId: existing.messageId || partial.messageId,
    };
  }
  return partial;
}

function readSelectionCache(
  accountId: number,
  folder: string,
  uid: number,
  preview?: MailMessageSummary | null
): MailMessageDetail | null {
  return readLocalBody(accountId, folder, uid, preview);
}

/**
 * Ouverture humaine simple :
 * 1. mémoire → paint immédiat
 * 2. pastille DD02 → check serveur (pas d’attente IDB)
 * 3. sinon IDB puis réseau
 */
export function useMailMessage(
  accountId?: number,
  uid?: number | null,
  folder = "INBOX",
  headerPreview?: MailMessageSummary | null
) {
  const selectionKey =
    accountId != null && uid != null && uid > 0 ? `${accountId}:${folder}:${uid}` : "";

  const [message, setMessage] = useState<MailMessageDetail | null>(() => {
    if (!accountId || !uid) return null;
    return readSelectionCache(accountId, folder, uid, headerPreview);
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchGenRef = useRef(0);
  const headerPreviewRef = useRef(headerPreview);
  headerPreviewRef.current = headerPreview;
  const selectionKeyRef = useRef(selectionKey);

  if (selectionKeyRef.current !== selectionKey) {
    selectionKeyRef.current = selectionKey;
    if (!accountId || !uid) {
      setMessage(null);
      setLoading(false);
      setError(null);
    } else {
      const preview = resolvePreviewForReader(headerPreview, accountId, uid);
      const hit = readSelectionCache(accountId, folder, uid, preview);
      if (hit) {
        setMessage(hit);
        setLoading(false);
        setError(null);
      } else {
        setMessage(preview ? mergePreviewWithExisting(preview, null, uid) : null);
        setLoading(true);
        setError(null);
      }
    }
  }

  const applyReadable = useCallback((body: MailMessageDetail) => {
    setMessage(body);
    setError(null);
    setLoading(false);
  }, []);

  const loadBody = useCallback(
    async (options: ReloadOptions = {}) => {
      if (!accountId || !uid) {
        setMessage(null);
        setLoading(false);
        setError(null);
        return;
      }

      const gen = ++fetchGenRef.current;
      const force = options.force === true;
      const preview = resolvePreviewForReader(headerPreviewRef.current, accountId, uid);
      const serverReady = Boolean(preview?.bodyCached);

      if (!force) {
        const memoryHit = readSelectionCache(accountId, folder, uid, preview);
        if (memoryHit) {
          applyReadable(memoryHit);
          return;
        }
        const idbHit = await readLocalBodyAsync(accountId, folder, uid, preview);
        if (gen !== fetchGenRef.current) return;
        if (idbHit) {
          applyReadable(idbHit);
          return;
        }
      }

      setLoading(true);
      setError(null);
      if (preview) {
        setMessage((prev) => mergePreviewWithExisting(preview, prev, uid));
      }

      pauseClientHydration();
      try {
        let next = await fetchMessageBodyWithCache(accountId, folder, uid, {
          force,
          interactive: options.interactive !== false,
          dd02Only: !force && serverReady,
          quick: !force && serverReady,
          timeoutMs: force ? 60_000 : 8_000,
        });
        if (gen !== fetchGenRef.current) return;

        if (
          !force &&
          serverReady &&
          !(next.message && isFetchedBodyComplete(next.message))
        ) {
          next = await fetchMessageBodyWithCache(accountId, folder, uid, {
            interactive: true,
            timeoutMs: 30_000,
          });
          if (gen !== fetchGenRef.current) return;
        }

        if (next.message && isFetchedBodyComplete(next.message)) {
          applyReadable(next.message);
          return;
        }

        setError(
          next.error ??
            "Impossible de charger le contenu. Vérifiez votre connexion ou réessayez."
        );
        setMessage((prev) => {
          if (messageMatchesReader(prev, uid, preview) && isFetchedBodyComplete(prev)) return prev;
          return preview ? summaryToPartialDetail(preview) : null;
        });
      } finally {
        resumeClientHydration();
        if (gen === fetchGenRef.current) setLoading(false);
      }
    },
    [accountId, applyReadable, folder, uid]
  );

  useEffect(() => {
    if (!accountId || !uid) {
      fetchGenRef.current += 1;
      return;
    }

    const gen = ++fetchGenRef.current;
    const preview = resolvePreviewForReader(headerPreviewRef.current, accountId, uid);
    const serverReady = Boolean(preview?.bodyCached);

    const memoryHit = readSelectionCache(accountId, folder, uid, preview);
    if (memoryHit) {
      applyReadable(memoryHit);
      return;
    }

    setLoading(true);
    setError(null);
    if (preview) {
      setMessage((prev) => mergePreviewWithExisting(preview, prev, uid));
    }

    void (async () => {
      pauseClientHydration();
      try {
        // Toujours IDB d’abord (même pastille verte) — local = instantané.
        const idbHit = await readLocalBodyAsync(accountId, folder, uid, preview);
        if (gen !== fetchGenRef.current) return;
        if (idbHit) {
          applyReadable(idbHit);
          return;
        }

        let hit = await fetchMessageBodyWithCache(accountId, folder, uid, {
          interactive: true,
          dd02Only: serverReady,
          quick: true,
          timeoutMs: 8_000,
        });
        if (gen !== fetchGenRef.current) return;

        if (serverReady && !(hit.message && isFetchedBodyComplete(hit.message))) {
          hit = await fetchMessageBodyWithCache(accountId, folder, uid, {
            interactive: true,
            timeoutMs: 30_000,
          });
          if (gen !== fetchGenRef.current) return;
        }

        if (hit.message && isFetchedBodyComplete(hit.message)) {
          applyReadable(hit.message);
          return;
        }

        setError(
          hit.error ??
            "Impossible de charger le contenu. Vérifiez votre connexion ou réessayez."
        );
        setMessage((prev) => {
          if (messageMatchesReader(prev, uid, preview) && isFetchedBodyComplete(prev)) return prev;
          return preview ? summaryToPartialDetail(preview) : null;
        });
      } finally {
        resumeClientHydration();
        if (gen === fetchGenRef.current) setLoading(false);
      }
    })();

    return () => {
      fetchGenRef.current += 1;
    };
  }, [accountId, applyReadable, folder, uid]);

  useEffect(() => {
    if (!accountId || !uid) return;
    return subscribeMessageBodyCache(() => {
      const preview = resolvePreviewForReader(headerPreviewRef.current, accountId, uid);
      const hit = readSelectionCache(accountId, folder, uid, preview);
      if (hit) applyReadable(hit);
    });
  }, [accountId, applyReadable, folder, uid]);

  const reload = useCallback(
    (options: ReloadOptions = {}) => {
      void loadBody(options);
    },
    [loadBody]
  );

  return { message, loading, error, reload };
}
